import crypto from 'crypto';
import axios, { AxiosRequestConfig, AxiosPromise } from 'axios';

interface CacheItem {
  id: string
  timestamp: number
  promise: AxiosPromise
  config: AxiosRequestConfig
}

interface CacheOptions {
  maxAge: number
}

export interface AxiosCacheRequestConfig extends AxiosRequestConfig {
  acceptCache?: CacheOptions | boolean
}

export class AxiosCache {

  // cache items store
  static _caches: CacheItem[] = [];

  // some caches should be removed on some events to avoid memory leak
  static _removeCache(cache: CacheItem) {
    AxiosCache._caches = AxiosCache._caches.filter(a => a !== cache);
  }

  // will derive an id that will serve as reference for retrieving cache
  static _getRequestId(config: AxiosRequestConfig): string {
    const { url, method, baseURL, headers, params, data } = config;
    const hash = crypto.createHash('md5');
    hash.update(JSON.stringify({ url, method, baseURL, headers, params, data }));
    return hash.digest().toString('hex');
  }

  // will create and adapter for caching
  static _createAdapter(options: CacheOptions): AxiosRequestConfig['adapter'] {
    return (config: AxiosRequestConfig): AxiosPromise => new Promise(async (resolve, reject) => {
      const id = AxiosCache._getRequestId(config);

      // check cache store
      for (const _cache of AxiosCache._caches) {
        const age = new Date().getTime() - _cache.timestamp;
        if (_cache.id === id) {
          if (!options.maxAge || age <= options.maxAge) {
            return resolve(_cache.promise);
          } else {
            AxiosCache._removeCache(_cache);
          }
          break;
        }
      }

      // ensure there's a default adapter
      const { adapter } = axios.defaults;
      if (!adapter) return reject(new Error('axios default adapter is undefined'));

      // create new request using default adapter
      const promise = adapter(config);
      const timestamp = new Date().getTime();
      const cache: CacheItem = { id, timestamp, promise, config };
      AxiosCache._caches.push(cache);

      // execute request
      try {
        await promise;
        return resolve(promise);
      } catch (e) {
        AxiosCache._removeCache(cache);
        reject(e);
      }
    });
  }

  // config getter to supply in axios functions
  static getConfig(config?: AxiosCacheRequestConfig): AxiosRequestConfig {
    const { acceptCache, ..._config } = config || {};
    if (acceptCache) {
      const minute = 60000;
      _config.adapter = AxiosCache._createAdapter(typeof acceptCache === 'object' ? acceptCache : {
        maxAge: 60 * minute
      });
    }
    return _config;
  };

  // reset caches
  static reset() {
    AxiosCache._caches = [];
  }
}

export default AxiosCache;
